import * as React from 'react';
import { ICheckoutClubInfoCollectorResources } from '../checkout-club-info-collector.props.autogenerated';

import { observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import { wineClubGiftDatePicker, wineClubGiftEndDate } from '../../../settings/app.settings';
import { GiftData } from '../types/gift-type';

export interface IGiftCollector {
    resources: ICheckoutClubInfoCollectorResources;
    allowEdits: boolean;
    configEndDates: wineClubGiftEndDate;
    configDatePicker: wineClubGiftDatePicker;
    formData: GiftData;
    onChange(giftInfo: GiftData): void;
}

/**
 * used to collect data about the current gift selections
 */
@observer
export class GiftCollector extends React.Component<IGiftCollector> {

    @observable private _firstName: string = '';
    @observable private _lastName: string = '';
    @observable private _email: string = '';
    @observable private _phoneNumber: string = '';
    @observable private _endDate: string = '';
    @observable private _customDate: string = '';
    @observable private _endDateSubmit: string = '';

    public constructor(props: IGiftCollector) {
        super(props);
        const formData: GiftData = props.formData || {};

        // populate form with any previous saved form data
        this._firstName = formData.firstName || '';
        this._lastName = formData.lastName || '';
        this._email = formData.email || '';
        this._phoneNumber = formData.phoneNumber || '';
        this._endDate = formData.endDate || '';
        this._customDate = formData.customDate || '';
        this._endDateSubmit = formData.endDateSubmit || '';

        this.props.onChange({
            firstName: this._firstName,
            lastName: this._lastName,
            email: this._email,
            phoneNumber: this._phoneNumber,
            endDate: this._endDate,
            customDate: this._customDate,
            endDateSubmit: this._endDateSubmit
        });

        this._onChange = this._onChange.bind(this);
        this._onChangeSelect = this._onChangeSelect.bind(this);
    }

    public render(): JSX.Element {
        const {
            allowEdits,
            resources: {
                checkoutClubInfoCollector__giftFirstName,
                checkoutClubInfoCollector__giftLastName,
                checkoutClubInfoCollector__giftEmail,
                checkoutClubInfoCollector__giftPhoneNumber,
                checkoutClubInfoCollector__giftEndDate
            }
        } = this.props;
        return (
            <div className='checkout-step-club-info-fields'>
                <div className='checkout-step-club-info-control'>
                    <label htmlFor='first-name'>{checkoutClubInfoCollector__giftFirstName}</label>
                    <input
                        type='text'
                        name='first-name'
                        className='checkout-step-input-first-name'
                        disabled={!allowEdits}
                        value={this._firstName}
                        data-map='_firstName'
                        onChange={this._onChange}
                        id='first-name'
                        required
                        aria-required='true'
                    />
                </div>
                <div className='checkout-step-club-info-control'>
                    <label htmlFor='last-name'>{checkoutClubInfoCollector__giftLastName}</label>
                    <input
                        type='text'
                        name='last-name'
                        className='checkout-step-input-last-name'
                        disabled={!allowEdits}
                        value={this._lastName}
                        data-map='_lastName'
                        onChange={this._onChange}
                        id='last-name'
                        required
                        aria-required='true'
                    />
                </div>
                <div className='checkout-step-club-info-control'>
                    <label htmlFor='email'>{checkoutClubInfoCollector__giftEmail}</label>
                    <input
                        type='text'
                        name='email'
                        className='checkout-step-input-email'
                        disabled={!allowEdits}
                        value={this._email}
                        data-map='_email'
                        onChange={this._onChange}
                        id='email'
                    />
                </div>
                <div className='checkout-step-club-info-control'>
                    <label htmlFor='phone-number'>{checkoutClubInfoCollector__giftPhoneNumber}</label>
                    <input
                        type='text'
                        name='phone-number'
                        className='checkout-step-input-phone-number'
                        disabled={!allowEdits}
                        value={this._phoneNumber}
                        data-map='_phoneNumber'
                        onChange={this._onChange}
                        id='phone-number'
                    />
                </div>
                { this.props.configEndDates || this.props.configDatePicker ?
                    <div className='checkout-step-club-info-control'>
                        <label htmlFor='end-date'>{checkoutClubInfoCollector__giftEndDate}</label>
                        <select
                            name='end-date'
                            className='checkout-step-input-end-date'
                            disabled={!allowEdits}
                            value={this._endDate}
                            data-map='_endDate'
                            onChange={this._onChangeSelect}
                            id='end-date'
                        >
                            {this.getEndDatePulldown()}
                        </select>
                        { this._endDate === 'datePicker' && this.props.configDatePicker ?
                            <input
                                id='end-date-datepicker'
                                type='date'
                                className='checkout-step-input-end-date-datepicker'
                                data-map='_customDate'
                                min={moment().add(this.props.configDatePicker.minType, this.props.configDatePicker.minLength || 0).format('YYYY-MM-DD')}
                                onChange={this._onChange}
                                value={this._customDate}
                            /> : ''
                        }
                    </div> : ''
                }
            </div>
        );
    }

    public getEndDatePulldown(): JSX.Element[] {
        const endDates = this.props.configEndDates;
        const datePicker = this.props.configDatePicker;
        const nodes = [] as JSX.Element[];

        // option for no end date
        nodes.push(<option value=''>{this.props.resources.checkoutClubInfoCollector__giftNoEndDate}</option>);

        // preconfigured options from global config
        if (endDates) {
            endDates.forEach(endDate => {
                const dateFormatted = moment().add(endDate.timeToExpireType, endDate.timeToExpire).format('MM/DD/YY');
                nodes.push(<option value={dateFormatted}>{endDate.name}</option>);
            });
        }

        // option for custom date with calendar
        if (datePicker && datePicker.name) {
            nodes.push(<option value='datePicker'>{datePicker.name}</option>);
        }
        return nodes;
    }

    private _onChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this[event.target.dataset.map!] = event.target.value;
        const formattedCustomDate = this._customDate ? moment(this._customDate).format('MM/DD/YYYY') : '';
        this._endDateSubmit = this._endDate !== 'datePicker' ? this._endDate : formattedCustomDate;

        this.props.onChange({
            firstName: this._firstName,
            lastName: this._lastName,
            email: this._email,
            phoneNumber: this._phoneNumber,
            endDate: this._endDate,
            customDate: this._customDate,
            endDateSubmit: this._endDateSubmit
        });
    }

    // needed to duplicate onChange to work with HTMLSelectElement type
    private _onChangeSelect(event: React.ChangeEvent<HTMLSelectElement>): void {
        this[event.target.dataset.map!] = event.target.value;
        const formattedCustomDate = this._customDate ? moment(this._customDate).format('MM/DD/YYYY') : '';
        this._endDateSubmit = this._endDate !== 'datePicker' ? this._endDate : formattedCustomDate;

        this.props.onChange({
            firstName: this._firstName,
            lastName: this._lastName,
            email: this._email,
            phoneNumber: this._phoneNumber,
            endDate: this._endDate,
            customDate: this._customDate,
            endDateSubmit: this._endDateSubmit
        });
    }
}
